import { useCallback, useEffect, useState } from "react";
import { getCountry, setCountry } from "../utils/countryStorage";
import { getCloudProgress, putCloudProgress } from "../utils/progressCloud";
import { useAuth } from "../context/AuthContext";
import { ProgressSyncStatus } from "./progressTypes";

const FEATURE_KEY = "country";

// The user's self-reported country, set on the profile page (an optional
// ISO 3166-1 alpha-2 code, e.g. "PT"). The JLPT test-site search reads this
// to prefill the search with the user's country. Mirrors useJlptLevel.ts
// near line-for-line - same cloud-sync/local-storage-fallback shape as every
// other client-trusted profile preference in this app.
export function useCountry() {
    const { status, user } = useAuth();
    const [country, setCountryState] = useState<string | null>(() => getCountry());
    const [syncStatus, setSyncStatus] = useState<ProgressSyncStatus>("local");

    useEffect(() => {
        if (status !== "authenticated" || !user?.identityId) {
            setSyncStatus("local");
            return undefined;
        }

        let cancelled = false;
        setSyncStatus("loading");

        getCloudProgress(user.identityId, FEATURE_KEY)
            .then((remote) => {
                if (cancelled) return;
                if (remote) {
                    const remoteCountry = remote.data ?? null;
                    setCountryState(remoteCountry);
                    setCountry(remoteCountry);
                }
                setSyncStatus("ready");
            })
            .catch(() => {
                if (!cancelled) setSyncStatus("error");
            });

        return () => {
            cancelled = true;
        };
    }, [status, user?.identityId]);

    const setUserCountry = useCallback(
        (nextCountry: string | null) => {
            setCountryState(nextCountry);
            setCountry(nextCountry);

            if (status === "authenticated" && user?.identityId) {
                putCloudProgress(user.identityId, FEATURE_KEY, nextCountry).catch(() => {
                    // Best-effort: the preference still applies locally for this session.
                });
            }
        },
        [status, user?.identityId]
    );

    return { country, setCountry: setUserCountry, syncStatus };
}
